import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import {
  configService,
  FulfilConfig,
  ShipHeroConfig,
  EmailConfig,
  User,
  ServiceCodeMappingItem,
} from "../services/configService";
import {
  Database,
  BarChart3,
  Save,
  CheckCircle,
  Users,
  Plus,
  Trash2,
  Edit,
  Mail,
  RefreshCw,
  Package,
} from "lucide-react";
import { formatDate } from "../utils/dateUtils";
import toast from "react-hot-toast";
import { useSearchParams } from "react-router-dom";
import { isTokenInvalidationError, isLoggingOut } from "../utils/errorUtils";

const Configuration: React.FC = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // Get initial tab from URL or default to fulfil
  const initialTab =
    (searchParams.get("tab") as
      | "fulfil"
      | "shiphero"
      | "email"
      | "servicecodes"
      | "users") || "fulfil";
  const [activeTab, setActiveTab] = useState<
    "fulfil" | "shiphero" | "email" | "servicecodes" | "users"
  >(initialTab);

  // Configuration states
  const [fulfilConfig, setFulfilConfig] = useState<FulfilConfig>({
    mode: "live",
    live: {
      subdomain: "",
      apiKey: "",
    },
    test: {
      subdomain: "",
      apiKey: "",
    },
  });

  const [shipheroConfig, setShipHeroConfig] = useState<ShipHeroConfig>({
    mode: "live",
    live: {
      refreshToken: "",
      oauthUrl: "https://public-api.shiphero.com/auth/refresh",
      apiBaseUrl: "https://public-api.shiphero.com",
      defaultWarehouseId: "",
      customerAccountId: "",
    },
    test: {
      refreshToken: "",
      oauthUrl: "https://public-api.shiphero.com/auth/refresh",
      apiBaseUrl: "https://public-api.shiphero.com",
      defaultWarehouseId: "",
      customerAccountId: "",
    },
  });

  const [emailConfig, setEmailConfig] = useState<EmailConfig>({
    smtpHost: "",
    smtpPort: 587,
    smtpUsername: "",
    smtpPassword: "",
    smtpUseTls: true,
    smtpUseSsl: false,
    fromEmail: "",
    fromName: "Fulfil ShipHero Mediator",
    isActive: false,
  });

  // Test connection states removed

  // Service code mapping states
  const [serviceCodeMappings, setServiceCodeMappings] = useState<
    ServiceCodeMappingItem[]
  >([]);
  const [serviceCodeMappingsOriginal, setServiceCodeMappingsOriginal] = useState<
    ServiceCodeMappingItem[]
  >([]);
  const [editingServiceCodeId, setEditingServiceCodeId] = useState<
    number | null
  >(null);
  const [editSnapshot, setEditSnapshot] =
    useState<ServiceCodeMappingItem | null>(null);
  const [serviceCodeMappingsLoading, setServiceCodeMappingsLoading] =
    useState(false);
  const [serviceCodeMappingsSaving, setServiceCodeMappingsSaving] =
    useState(false);
  const [serviceCodeDeletePending, setServiceCodeDeletePending] = useState<{
    index: number;
    row: ServiceCodeMappingItem;
  } | null>(null);

  // User management states
  const [users, setUsers] = useState<User[]>([]);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [newUser, setNewUser] = useState({
    username: "",
    email: "",
    password: "",
    role: "admin" as "admin" | "staff",
    name: "",
  });

  useEffect(() => {
    loadConfiguration();
    if (user?.role === "superadmin") {
      loadUsers();
    }
  }, [user]);

  useEffect(() => {
    if (activeTab === "servicecodes") {
      loadServiceCodeMappings();
    }
  }, [activeTab]);

  const loadServiceCodeMappings = async () => {
    try {
      setServiceCodeMappingsLoading(true);
      const res = await configService.getServiceCodeMappings();
      const list = res.mappings || [];
      setServiceCodeMappings(list);
      setServiceCodeMappingsOriginal(list.map((m) => ({ ...m })));
      setEditingServiceCodeId(null);
      setEditSnapshot(null);
    } catch (error) {
      if (!isTokenInvalidationError(error) && !isLoggingOut()) {
        toast.error("Failed to load service code mappings");
      }
    } finally {
      setServiceCodeMappingsLoading(false);
    }
  };

  const handleSaveServiceCodeMappings = async () => {
    try {
      setServiceCodeMappingsSaving(true);
      const currentIds = new Set(
        serviceCodeMappings
          .filter((m) => m.id != null)
          .map((m) => m.id as number)
      );
      const added = serviceCodeMappings
        .filter((m) => m.id == null && (m.serviceCode || "").trim() !== "")
        .map((m) => ({
          serviceCode: (m.serviceCode || "").trim(),
          carrier: (m.carrier || "").trim(),
          shippingMethod: (m.shippingMethod || "").trim(),
          wholesaleShippingOption: (m.wholesaleShippingOption || "").trim() || undefined,
        }));
      const deleted = serviceCodeMappingsOriginal
        .filter((m) => m.id != null && !currentIds.has(m.id!))
        .map((m) => m.id as number);
      const byId = new Map(
        serviceCodeMappingsOriginal
          .filter((m) => m.id != null)
          .map((m) => [m.id!, m] as [number, ServiceCodeMappingItem])
      );
      const updated = serviceCodeMappings
        .filter((m) => m.id != null && (m.serviceCode || "").trim() !== "")
        .filter((m) => {
          const orig = byId.get(m.id!);
          if (!orig) return true;
          return (
            (m.serviceCode || "").trim() !== (orig.serviceCode || "").trim() ||
            (m.carrier || "").trim() !== (orig.carrier || "").trim() ||
            (m.shippingMethod || "").trim() !== (orig.shippingMethod || "").trim() ||
            (m.wholesaleShippingOption || "").trim() !== (orig.wholesaleShippingOption || "").trim()
          );
        })
        .map((m) => ({
          id: m.id,
          serviceCode: (m.serviceCode || "").trim(),
          carrier: (m.carrier || "").trim(),
          shippingMethod: (m.shippingMethod || "").trim(),
          wholesaleShippingOption: (m.wholesaleShippingOption || "").trim(),
        }));

      if (added.length === 0 && updated.length === 0 && deleted.length === 0) {
        toast.success("No changes to save");
        return;
      }
      await configService.patchServiceCodeMappings({
        added: added.length ? added : undefined,
        updated: updated.length ? updated : undefined,
        deleted: deleted.length ? deleted : undefined,
      });
      toast.success("Service code mappings saved successfully");
      loadServiceCodeMappings();
    } catch (error) {
      if (!isTokenInvalidationError(error) && !isLoggingOut()) {
        toast.error(
          error instanceof Error
            ? error.message
            : "Failed to save service code mappings"
        );
      }
    } finally {
      setServiceCodeMappingsSaving(false);
    }
  };

  const startEditServiceCode = (row: ServiceCodeMappingItem) => {
    if (row.id == null) return;
    setEditingServiceCodeId(row.id);
    setEditSnapshot({ ...row });
  };

  const cancelEditServiceCode = () => {
    if (editingServiceCodeId == null || editSnapshot == null) return;
    setServiceCodeMappings((prev) =>
      prev.map((m) =>
        m.id === editingServiceCodeId ? { ...editSnapshot } : m
      )
    );
    setEditingServiceCodeId(null);
    setEditSnapshot(null);
  };

  const addServiceCodeMappingRow = () => {
    setServiceCodeMappings([
      ...serviceCodeMappings,
      { serviceCode: "", carrier: "", shippingMethod: "" },
    ]);
  };

  const updateServiceCodeMapping = (
    index: number,
    field: keyof ServiceCodeMappingItem,
    value: string
  ) => {
    const next = [...serviceCodeMappings];
    if (!next[index]) return;
    if (field === "serviceCode") next[index].serviceCode = value;
    else if (field === "carrier") next[index].carrier = value;
    else if (field === "shippingMethod") next[index].shippingMethod = value;
    else if (field === "wholesaleShippingOption") next[index].wholesaleShippingOption = value;
    setServiceCodeMappings(next);
  };

  const requestRemoveServiceCodeMapping = (index: number) => {
    const row = serviceCodeMappings[index];
    if (row) setServiceCodeDeletePending({ index, row });
  };

  const cancelRemoveServiceCodeMapping = () => {
    setServiceCodeDeletePending(null);
  };

  const confirmRemoveServiceCodeMapping = async () => {
    if (serviceCodeDeletePending == null) return;
    const { index, row } = serviceCodeDeletePending;
    setServiceCodeDeletePending(null);
    try {
      if (row.id != null) {
        setServiceCodeMappingsSaving(true);
        await configService.patchServiceCodeMappings({ deleted: [row.id] });
        toast.success("Service code mapping deleted");
        await loadServiceCodeMappings();
        if (row.id === editingServiceCodeId) {
          setEditingServiceCodeId(null);
          setEditSnapshot(null);
        }
      } else {
        setServiceCodeMappings((prev) => prev.filter((_, i) => i !== index));
      }
    } catch (error) {
      if (!isTokenInvalidationError(error) && !isLoggingOut()) {
        toast.error(
          error instanceof Error ? error.message : "Failed to delete mapping"
        );
      }
    } finally {
      setServiceCodeMappingsSaving(false);
    }
  };

  const loadDefaultServiceCodeMappings = () => {
    const defaults: ServiceCodeMappingItem[] = [
      { serviceCode: "UPS_SUREPOST", carrier: "UPS", shippingMethod: "UPS Surepost" },
      { serviceCode: "UPS_GROUND", carrier: "UPS", shippingMethod: "UPS Ground" },
      { serviceCode: "UPS_3_DAY_SELECT", carrier: "UPS", shippingMethod: "UPS 3 Day Select" },
      { serviceCode: "UPS_2ND_DAY_AIR", carrier: "UPS", shippingMethod: "UPS 2nd Day Air" },
      { serviceCode: "UPS_2ND_DAY_AIR_AM", carrier: "UPS", shippingMethod: "UPS 2nd Day Air AM" },
      { serviceCode: "UPS_NEXT_DAY_AIR_SAVER", carrier: "UPS", shippingMethod: "UPS Next Day Air Saver" },
      { serviceCode: "UPS_NEXT_DAY_AIR", carrier: "UPS", shippingMethod: "UPS Next Day Air" },
      { serviceCode: "UPS_NEXT_DAY_AIR_EARLY_AM", carrier: "UPS", shippingMethod: "UPS Next Day Air Early AM" },
      { serviceCode: "FEDEX_GROUND_ECONOMY", carrier: "Fedex", shippingMethod: "Fedex SmartPost" },
      { serviceCode: "HOME_DELIVERY", carrier: "Fedex", shippingMethod: "Fedex Ground Services" },
      { serviceCode: "FEDEX_GROUND", carrier: "Fedex", shippingMethod: "Fedex Ground Services" },
      { serviceCode: "EXPRESS_SAVER", carrier: "Fedex", shippingMethod: "Fedex Express Saver" },
      { serviceCode: "FEDEX_2_DAY", carrier: "Fedex", shippingMethod: "Fedex 2 Day" },
      { serviceCode: "FEDEX_2_DAY_AM", carrier: "Fedex", shippingMethod: "Fedex 2 Day AM" },
      { serviceCode: "STANDARD_OVERNIGHT", carrier: "Fedex", shippingMethod: "Fedex Standard Overnight" },
      { serviceCode: "PRIORITY_OVERNIGHT", carrier: "Fedex", shippingMethod: "Fedex Priority Overnight" },
      { serviceCode: "FIRST_OVERNIGHT", carrier: "Fedex", shippingMethod: "Fedex First Overnight" },
      { serviceCode: "LTL", carrier: "Wholesale Generic", shippingMethod: "Wholesale Generic Label" },
    ];
    const existingCodes = new Set(
      serviceCodeMappings
        .map((m) => (m.serviceCode || "").trim().toLowerCase())
        .filter(Boolean)
    );
    const missing = defaults.filter(
      (d) => !existingCodes.has((d.serviceCode || "").trim().toLowerCase())
    );
    if (missing.length === 0) {
      toast.success("All default mappings are already in the list.");
      return;
    }
    setServiceCodeMappings((prev) => [...prev, ...missing]);
    toast.success(
      `Added ${missing.length} missing default mapping${missing.length === 1 ? "" : "s"}. Click Save to apply.`
    );
  };

  // Update URL when tab changes
  useEffect(() => {
    setSearchParams({ tab: activeTab });
  }, [activeTab, setSearchParams]);

  const loadConfiguration = async () => {
    try {
      setIsLoading(true);
      const config = await configService.getConfig();
      setFulfilConfig(config.fulfil);
      setShipHeroConfig(config.shiphero);

      // Load email configuration
      try {
        const emailConfigData = await configService.getEmailConfig();
        setEmailConfig(emailConfigData);
      } catch (error) {
        console.log("Email configuration not available yet");
      }
    } catch (error) {
      if (!isTokenInvalidationError(error) && !isLoggingOut()) {
        toast.error("Failed to load configuration");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const res = await configService.getUsers(1, 50);
      setUsers(res.items);
    } catch (error) {
      if (!isTokenInvalidationError(error) && !isLoggingOut()) {
        toast.error("Failed to load users");
      }
    }
  };

  const handleSaveFulfil = async () => {
    try {
      setIsSaving(true);
      await configService.updateFulfilConfig(fulfilConfig);
      toast.success("Fulfil configuration saved successfully");
    } catch (error) {
      if (!isTokenInvalidationError(error) && !isLoggingOut()) {
        toast.error(
          error instanceof Error
            ? error.message
            : "Failed to save Fulfil configuration"
        );
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveShipHero = async () => {
    try {
      setIsSaving(true);
      await configService.updateShipHeroConfig(shipheroConfig);
      toast.success("ShipHero configuration saved successfully");
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to save ShipHero configuration"
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveEmail = async () => {
    try {
      setIsSaving(true);
      await configService.updateEmailConfig(emailConfig);
      toast.success("Email configuration saved successfully");
      // Reload email config to get updated state
      const updatedConfig = await configService.getEmailConfig();
      setEmailConfig(updatedConfig);
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to save email configuration"
      );
    } finally {
      setIsSaving(false);
    }
  };

  // Test handlers removed

  const handleCreateUser = async () => {
    try {
      if (!newUser.username || !newUser.email || !newUser.password) {
        toast.error("Please fill in all required fields");
        return;
      }

      await configService.createUser({
        username: newUser.username,
        email: newUser.email,
        password: newUser.password,
        role: newUser.role,
        name: newUser.name || undefined,
      });
      toast.success("User created successfully");
      setNewUser({ username: "", email: "", password: "", role: "admin", name: "" });
      setShowCreateUser(false);
      loadUsers();
    } catch (error) {
      if (!isTokenInvalidationError(error) && !isLoggingOut()) {
        toast.error(
          error instanceof Error ? error.message : "Failed to create user"
        );
      }
    }
  };

  const tabs = [
    { id: "fulfil", name: "Fulfil Configuration", icon: Database },
    { id: "shiphero", name: "ShipHero Configuration", icon: BarChart3 },
    { id: "email", name: "Email Configuration", icon: Mail },
    { id: "servicecodes", name: "Service Code Mapping", icon: Package },
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 text-primary-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Configuration</h1>
        <p className="mt-2 text-sm text-gray-700">
          Manage your Fulfil, ShipHero, and email settings.
          {user?.is_admin && (
            <span className="ml-2 text-primary-600 font-medium">
              (Admin Access)
            </span>
          )}
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? "border-primary-500 text-primary-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                <Icon className="h-4 w-4 inline mr-2" />
                {tab.name}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="bg-white shadow rounded-lg">
        {activeTab === "fulfil" && (
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
              Fulfil Configuration
            </h3>

            {/* Mode Selection */}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Live Configuration */}
              <div className="space-y-4">
                <h4 className="text-md font-medium text-gray-900 border-b pb-2">
                  Live Environment
                </h4>
                <div>
                  <label
                    htmlFor="fulfil-live-subdomain"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Subdomain
                  </label>
                  <input
                    type="text"
                    id="fulfil-live-subdomain"
                    value={fulfilConfig.live.subdomain}
                    onChange={(e) =>
                      setFulfilConfig({
                        ...fulfilConfig,
                        live: {
                          ...fulfilConfig.live,
                          subdomain: e.target.value,
                        },
                      })
                    }
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    placeholder="yourcompany"
                  />
                </div>
                <div>
                  <label
                    htmlFor="fulfil-live-api-key"
                    className="block text-sm font-medium text-gray-700"
                  >
                    API Key
                  </label>
                  <input
                    type="text"
                    id="fulfil-live-api-key"
                    value={fulfilConfig.live.apiKey}
                    onChange={(e) =>
                      setFulfilConfig({
                        ...fulfilConfig,
                        live: { ...fulfilConfig.live, apiKey: e.target.value },
                      })
                    }
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    placeholder="Enter your Fulfil Live API key"
                  />
                </div>
              </div>

              {/* Test Configuration */}
              <div className="space-y-4">
                <h4 className="text-md font-medium text-gray-900 border-b pb-2">
                  Test Environment
                </h4>
                <div>
                  <label
                    htmlFor="fulfil-test-subdomain"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Subdomain
                  </label>
                  <input
                    type="text"
                    id="fulfil-test-subdomain"
                    value={fulfilConfig.test.subdomain}
                    onChange={(e) =>
                      setFulfilConfig({
                        ...fulfilConfig,
                        test: {
                          ...fulfilConfig.test,
                          subdomain: e.target.value,
                        },
                      })
                    }
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    placeholder="yourcompany-test"
                  />
                </div>
                <div>
                  <label
                    htmlFor="fulfil-test-api-key"
                    className="block text-sm font-medium text-gray-700"
                  >
                    API Key
                  </label>
                  <input
                    type="text"
                    id="fulfil-test-api-key"
                    value={fulfilConfig.test.apiKey}
                    onChange={(e) =>
                      setFulfilConfig({
                        ...fulfilConfig,
                        test: { ...fulfilConfig.test, apiKey: e.target.value },
                      })
                    }
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    placeholder="Enter your Fulfil Test API key"
                  />
                </div>
              </div>
            </div>

            <div className="flex space-x-3 mt-6">
              <button
                onClick={handleSaveFulfil}
                disabled={isSaving}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save className="h-4 w-4 mr-2" />
                {isSaving ? "Saving..." : "Save Configuration"}
              </button>
            </div>
          </div>
        )}

        {activeTab === "shiphero" && (
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
              ShipHero Configuration
            </h3>

            {/* Mode Selection */}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Live Configuration */}
              <div className="space-y-4">
                <h4 className="text-md font-medium text-gray-900 border-b pb-2">
                  Live Environment
                </h4>
                <div>
                  <label
                    htmlFor="shiphero-live-refresh-token"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Refresh Token
                  </label>
                  <input
                    type="text"
                    id="shiphero-live-refresh-token"
                    value={shipheroConfig.live.refreshToken}
                    onChange={(e) =>
                      setShipHeroConfig({
                        ...shipheroConfig,
                        live: {
                          ...shipheroConfig.live,
                          refreshToken: e.target.value,
                        },
                      })
                    }
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    placeholder="Enter your ShipHero live refresh token"
                  />
                </div>
                <div>
                  <label
                    htmlFor="shiphero-live-oauth-url"
                    className="block text-sm font-medium text-gray-700"
                  >
                    OAuth URL
                  </label>
                  <input
                    type="url"
                    id="shiphero-live-oauth-url"
                    value={shipheroConfig.live.oauthUrl}
                    onChange={(e) =>
                      setShipHeroConfig({
                        ...shipheroConfig,
                        live: {
                          ...shipheroConfig.live,
                          oauthUrl: e.target.value,
                        },
                      })
                    }
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  />
                </div>
                <div>
                  <label
                    htmlFor="shiphero-live-api-base-url"
                    className="block text-sm font-medium text-gray-700"
                  >
                    API Base URL
                  </label>
                  <input
                    type="url"
                    id="shiphero-live-api-base-url"
                    value={shipheroConfig.live.apiBaseUrl}
                    onChange={(e) =>
                      setShipHeroConfig({
                        ...shipheroConfig,
                        live: {
                          ...shipheroConfig.live,
                          apiBaseUrl: e.target.value,
                        },
                      })
                    }
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  />
                </div>
                <div>
                  <label
                    htmlFor="shiphero-live-default-warehouse-id"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Default Warehouse ID
                  </label>
                  <input
                    type="text"
                    id="shiphero-live-default-warehouse-id"
                    value={shipheroConfig.live.defaultWarehouseId || ""}
                    onChange={(e) =>
                      setShipHeroConfig({
                        ...shipheroConfig,
                        live: {
                          ...shipheroConfig.live,
                          defaultWarehouseId: e.target.value,
                        },
                      })
                    }
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    placeholder="V2FyZWhvdXNlOjEyMzQ="
                  />
                </div>
                <div>
                  <label
                    htmlFor="shiphero-live-customer-account-id"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Customer Account
                  </label>
                  <input
                    type="text"
                    id="shiphero-live-customer-account-id"
                    value={shipheroConfig.live.customerAccountId || ""}
                    onChange={(e) =>
                      setShipHeroConfig({
                        ...shipheroConfig,
                        live: {
                          ...shipheroConfig.live,
                          customerAccountId: e.target.value,
                        },
                      })
                    }
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    placeholder="3PL customer account ID (optional)"
                  />
                </div>
              </div>

              {/* Test Configuration */}
              <div className="space-y-4">
                <h4 className="text-md font-medium text-gray-900 border-b pb-2">
                  Test Environment
                </h4>
                <div>
                  <label
                    htmlFor="shiphero-test-refresh-token"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Refresh Token
                  </label>
                  <input
                    type="text"
                    id="shiphero-test-refresh-token"
                    value={shipheroConfig.test.refreshToken}
                    onChange={(e) =>
                      setShipHeroConfig({
                        ...shipheroConfig,
                        test: {
                          ...shipheroConfig.test,
                          refreshToken: e.target.value,
                        },
                      })
                    }
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    placeholder="Enter your ShipHero test refresh token"
                  />
                </div>
                <div>
                  <label
                    htmlFor="shiphero-test-oauth-url"
                    className="block text sm font-medium text-gray-700"
                  >
                    OAuth URL
                  </label>
                  <input
                    type="url"
                    id="shiphero-test-oauth-url"
                    value={shipheroConfig.test.oauthUrl}
                    onChange={(e) =>
                      setShipHeroConfig({
                        ...shipheroConfig,
                        test: {
                          ...shipheroConfig.test,
                          oauthUrl: e.target.value,
                        },
                      })
                    }
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    placeholder="V2FyZWhvdXNlOjEyMzQ="
                  />
                </div>
                <div>
                  <label
                    htmlFor="shiphero-test-api-base-url"
                    className="block text-sm font-medium text-gray-700"
                  >
                    API Base URL
                  </label>
                  <input
                    type="url"
                    id="shiphero-test-api-base-url"
                    value={shipheroConfig.test.apiBaseUrl}
                    onChange={(e) =>
                      setShipHeroConfig({
                        ...shipheroConfig,
                        test: {
                          ...shipheroConfig.test,
                          apiBaseUrl: e.target.value,
                        },
                      })
                    }
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  />
                </div>
                <div>
                  <label
                    htmlFor="shiphero-test-default-warehouse-id"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Default Warehouse ID
                  </label>
                  <input
                    type="text"
                    id="shiphero-test-default-warehouse-id"
                    value={shipheroConfig.test.defaultWarehouseId || ""}
                    onChange={(e) =>
                      setShipHeroConfig({
                        ...shipheroConfig,
                        test: {
                          ...shipheroConfig.test,
                          defaultWarehouseId: e.target.value,
                        },
                      })
                    }
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    placeholder="V2FyZWhvdXNlOjEyMzQ="
                  />
                </div>
                <div>
                  <label
                    htmlFor="shiphero-test-customer-account-id"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Customer Account
                  </label>
                  <input
                    type="text"
                    id="shiphero-test-customer-account-id"
                    value={shipheroConfig.test.customerAccountId || ""}
                    onChange={(e) =>
                      setShipHeroConfig({
                        ...shipheroConfig,
                        test: {
                          ...shipheroConfig.test,
                          customerAccountId: e.target.value,
                        },
                      })
                    }
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    placeholder="3PL customer account ID (optional)"
                  />
                </div>
              </div>
            </div>

            <div className="flex space-x-3 mt-6">
              <button
                onClick={handleSaveShipHero}
                disabled={isSaving}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save className="h-4 w-4 mr-2" />
                {isSaving ? "Saving..." : "Save Configuration"}
              </button>
            </div>
          </div>
        )}


        {activeTab === "email" && (
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
              Email Configuration
            </h3>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label
                    htmlFor="smtp-host"
                    className="block text-sm font-medium text-gray-700"
                  >
                    SMTP Host
                  </label>
                  <input
                    type="text"
                    id="smtp-host"
                    value={emailConfig.smtpHost}
                    onChange={(e) =>
                      setEmailConfig({
                        ...emailConfig,
                        smtpHost: e.target.value,
                      })
                    }
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    placeholder="smtp.gmail.com"
                  />
                </div>
                <div>
                  <label
                    htmlFor="smtp-port"
                    className="block text-sm font-medium text-gray-700"
                  >
                    SMTP Port
                  </label>
                  <input
                    type="number"
                    id="smtp-port"
                    value={emailConfig.smtpPort}
                    onChange={(e) =>
                      setEmailConfig({
                        ...emailConfig,
                        smtpPort: parseInt(e.target.value) || 587,
                      })
                    }
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label
                    htmlFor="smtp-username"
                    className="block text-sm font-medium text-gray-700"
                  >
                    SMTP Username
                  </label>
                  <input
                    type="text"
                    id="smtp-username"
                    value={emailConfig.smtpUsername}
                    onChange={(e) =>
                      setEmailConfig({
                        ...emailConfig,
                        smtpUsername: e.target.value,
                      })
                    }
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    placeholder="your-email@gmail.com"
                  />
                </div>
                <div>
                  <label
                    htmlFor="smtp-password"
                    className="block text-sm font-medium text-gray-700"
                  >
                    SMTP Password
                  </label>
                  <input
                    type="text"
                    id="smtp-password"
                    value={emailConfig.smtpPassword}
                    onChange={(e) =>
                      setEmailConfig({
                        ...emailConfig,
                        smtpPassword: e.target.value,
                      })
                    }
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    placeholder="Enter your SMTP password"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label
                    htmlFor="smtp-use-tls"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Use TLS
                  </label>
                  <input
                    type="checkbox"
                    id="smtp-use-tls"
                    checked={emailConfig.smtpUseTls}
                    onChange={(e) =>
                      setEmailConfig({
                        ...emailConfig,
                        smtpUseTls: e.target.checked,
                      })
                    }
                    className="mt-1"
                  />
                </div>
                <div>
                  <label
                    htmlFor="smtp-use-ssl"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Use SSL
                  </label>
                  <input
                    type="checkbox"
                    id="smtp-use-ssl"
                    checked={emailConfig.smtpUseSsl}
                    onChange={(e) =>
                      setEmailConfig({
                        ...emailConfig,
                        smtpUseSsl: e.target.checked,
                      })
                    }
                    className="mt-1"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label
                    htmlFor="from-email"
                    className="block text-sm font-medium text-gray-700"
                  >
                    From Email
                  </label>
                  <input
                    type="email"
                    id="from-email"
                    value={emailConfig.fromEmail}
                    onChange={(e) =>
                      setEmailConfig({
                        ...emailConfig,
                        fromEmail: e.target.value,
                      })
                    }
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    placeholder="noreply@example.com"
                  />
                </div>
                <div>
                  <label
                    htmlFor="from-name"
                    className="block text-sm font-medium text-gray-700"
                  >
                    From Name
                  </label>
                  <input
                    type="text"
                    id="from-name"
                    value={emailConfig.fromName}
                    onChange={(e) =>
                      setEmailConfig({
                        ...emailConfig,
                        fromName: e.target.value,
                      })
                    }
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    placeholder="Your Company Name"
                  />
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="email-active"
                  checked={emailConfig.isActive}
                  onChange={(e) =>
                    setEmailConfig({
                      ...emailConfig,
                      isActive: e.target.checked,
                    })
                  }
                  className="h-4 w-4 text-primary-600 border-gray-300 rounded"
                />
                <label htmlFor="email-active" className="text-sm text-gray-700">
                  Activate email notifications
                </label>
              </div>

              <div className="flex space-x-3 mt-6">
                <button
                  onClick={handleSaveEmail}
                  disabled={isSaving}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {isSaving ? "Saving..." : "Save Configuration"}
                </button>
              </div>
            </div>
          </div>
        )}

        {activeTab === "servicecodes" && (
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-2">
              Service Code Mapping
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Map Fulfil <code className="bg-gray-100 px-1 rounded">service_code</code> to ShipHero{" "}
              <code className="bg-gray-100 px-1 rounded">carrier</code> and{" "}
              <code className="bg-gray-100 px-1 rounded">shipping_method</code>. Matching is case-insensitive and takes precedence over channel defaults.
            </p>
            <div className="flex flex-wrap gap-2 mb-4">
              <button
                type="button"
                onClick={addServiceCodeMappingRow}
                className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add row
              </button>
              <button
                type="button"
                onClick={loadDefaultServiceCodeMappings}
                className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                Load default mappings
              </button>
              <button
                type="button"
                onClick={handleSaveServiceCodeMappings}
                disabled={serviceCodeMappingsSaving || serviceCodeMappingsLoading}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save className="h-4 w-4 mr-2" />
                {serviceCodeMappingsSaving ? "Saving..." : "Save mappings"}
              </button>
            </div>
            {serviceCodeMappingsLoading ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="h-6 w-6 text-primary-600 animate-spin" />
              </div>
            ) : (
              <div className="overflow-x-auto shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Fulfil service_code
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        ShipHero carrier
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        ShipHero method
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Wholesale Shipping Option
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-28">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {serviceCodeMappings.length === 0 && (
                      <tr>
                        <td colSpan={5} className="px-4 py-6 text-center text-sm text-gray-500">
                          No mappings. Add a row or load default mappings.
                        </td>
                      </tr>
                    )}
                    {serviceCodeMappings.map((row, index) => {
                      const hasId = row.id != null;
                      const isEditing =
                        hasId && editingServiceCodeId === row.id;
                      const isDisabled = hasId && !isEditing;
                      const inputClass =
                        "block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm disabled:bg-gray-100 disabled:text-gray-600 disabled:cursor-not-allowed";
                      return (
                        <tr key={row.id ?? `new-${index}`}>
                          <td className="px-4 py-2">
                            <input
                              type="text"
                              value={row.serviceCode}
                              onChange={(e) =>
                                updateServiceCodeMapping(
                                  index,
                                  "serviceCode",
                                  e.target.value
                                )
                              }
                              disabled={isDisabled}
                              className={inputClass}
                              placeholder="e.g. UPS_GROUND"
                            />
                          </td>
                          <td className="px-4 py-2">
                            <input
                              type="text"
                              value={row.carrier}
                              onChange={(e) =>
                                updateServiceCodeMapping(
                                  index,
                                  "carrier",
                                  e.target.value
                                )
                              }
                              disabled={isDisabled}
                              className={inputClass}
                              placeholder="e.g. UPS"
                            />
                          </td>
                          <td className="px-4 py-2">
                            <input
                              type="text"
                              value={row.shippingMethod}
                              onChange={(e) =>
                                updateServiceCodeMapping(
                                  index,
                                  "shippingMethod",
                                  e.target.value
                                )
                              }
                              disabled={isDisabled}
                              className={inputClass}
                              placeholder="e.g. UPS Ground"
                            />
                          </td>
                          <td className="px-4 py-2">
                            <select
                              value={row.wholesaleShippingOption || ""}
                              onChange={(e) =>
                                updateServiceCodeMapping(
                                  index,
                                  "wholesaleShippingOption",
                                  e.target.value
                                )
                              }
                              disabled={isDisabled}
                              className={inputClass}
                            >
                              <option value="">(None)</option>
                              <option value="COURIER">COURIER</option>
                              <option value="FREIGHT">FREIGHT</option>
                            </select>
                          </td>
                          <td className="px-4 py-2 text-right whitespace-nowrap">
                            {hasId ? (
                              isEditing ? (
                                <>
                                  <button
                                    type="button"
                                    onClick={cancelEditServiceCode}
                                    className="text-gray-600 hover:text-gray-900 mr-2"
                                    title="Cancel"
                                  >
                                    Cancel
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      requestRemoveServiceCodeMapping(index)
                                    }
                                    className="text-red-600 hover:text-red-900"
                                    title="Remove row"
                                  >
                                    <Trash2 className="h-4 w-4 inline" />
                                  </button>
                                </>
                              ) : (
                                <>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      startEditServiceCode(row)
                                    }
                                    className="text-primary-600 hover:text-primary-900 mr-2"
                                    title="Edit row"
                                  >
                                    <Edit className="h-4 w-4 inline" />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      requestRemoveServiceCodeMapping(index)
                                    }
                                    className="text-red-600 hover:text-red-900"
                                    title="Remove row"
                                  >
                                    <Trash2 className="h-4 w-4 inline" />
                                  </button>
                                </>
                              )
                            ) : (
                              <button
                                type="button"
                                onClick={() =>
                                  requestRemoveServiceCodeMapping(index)
                                }
                                className="text-red-600 hover:text-red-900"
                                title="Remove row"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {serviceCodeDeletePending != null && (
              <div
                className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
                role="dialog"
                aria-modal="true"
                aria-labelledby="service-code-delete-title"
              >
                <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-5">
                  <h3
                    id="service-code-delete-title"
                    className="text-lg font-medium text-gray-900 mb-2"
                  >
                    Delete service code mapping?
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">
                    <span className="font-mono font-medium">
                      {serviceCodeDeletePending.row.serviceCode || "(empty)"}
                    </span>
                    {" → "}
                    {serviceCodeDeletePending.row.carrier || ""} /{" "}
                    {serviceCodeDeletePending.row.shippingMethod || ""}
                  </p>
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={cancelRemoveServiceCodeMapping}
                      className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={confirmRemoveServiceCodeMapping}
                      disabled={serviceCodeMappingsSaving}
                      className="px-3 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {serviceCodeMappingsSaving ? "Deleting..." : "Delete"}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === "users" && user?.role === "superadmin" && (
          <div className="px-4 py-5 sm:p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                User Management
              </h3>
              <button
                onClick={() => setShowCreateUser(true)}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add User
              </button>
            </div>

            {/* Users List */}
            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Role
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {users.map((userItem) => (
                    <tr key={userItem.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {userItem.username}
                          </div>
                          <div className="text-sm text-gray-500">
                            {userItem.email}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
                          {(userItem.role || (userItem.is_admin ? "admin" : "staff"))?.toUpperCase()}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            userItem.is_active
                              ? "bg-green-100 text-green-800"
                              : "bg-red-100 text-red-800"
                          }`}
                        >
                          {userItem.is_active ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {userItem.created_at
                          ? formatDate(userItem.created_at)
                          : "N/A"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button className="text-primary-600 hover:text-primary-900 mr-3">
                          <Edit className="h-4 w-4" />
                        </button>
                        <button className="text-red-600 hover:text-red-900">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Create User Modal */}
            {showCreateUser && (
              <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
                <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
                  <div className="mt-3">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">
                      Create New User
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Username
                        </label>
                        <input
                          type="text"
                          value={newUser.username}
                          onChange={(e) =>
                            setNewUser({ ...newUser, username: e.target.value })
                          }
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Name
                        </label>
                        <input
                          type="text"
                          value={newUser.name}
                          onChange={(e) =>
                            setNewUser({ ...newUser, name: e.target.value })
                          }
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Email
                        </label>
                        <input
                          type="email"
                          value={newUser.email}
                          onChange={(e) =>
                            setNewUser({ ...newUser, email: e.target.value })
                          }
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Password
                        </label>
                        <input
                          type="password"
                          value={newUser.password}
                          onChange={(e) =>
                            setNewUser({ ...newUser, password: e.target.value })
                          }
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Role
                        </label>
                        <select
                          value={newUser.role}
                          onChange={(e) =>
                            setNewUser({
                              ...newUser,
                              role: e.target.value as "admin" | "staff",
                            })
                          }
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                        >
                          <option value="admin">Admin</option>
                          <option value="staff">Staff</option>
                        </select>
                      </div>
                    </div>
                    <div className="flex justify-end space-x-3 mt-6">
                      <button
                        onClick={() => setShowCreateUser(false)}
                        className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleCreateUser}
                        className="px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
                      >
                        Create User
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Configuration;
